import type { Hip3Market, Quote, Position } from '../types';

const HYPERLIQUID_API = 'https://api.hyperliquid.xyz/info';

interface L2BookLevel {
  px: string;
  sz: string;
  n: number;
}

interface L2BookResponse {
  coin: string;
  time: number;
  levels: [L2BookLevel[], L2BookLevel[]]; // [bids, asks]
}

interface AllMidsResponse {
  [coin: string]: string;
}

interface OpenOrderResponse {
  coin: string;
  limitPx: string;
  oid: number;
  side: 'A' | 'B'; // A = Ask/Sell, B = Bid/Buy
  sz: string;
  timestamp: number;
}

interface UserFillResponse {
  closedPnl: string;
  coin: string;
  dir: string;
  px: string;
  sz: string;
  fee: string;
  tid: number;
  time: number;
  startPosition: string;
  oid: number;
  crossed: boolean;
  hash: string;
}

interface FundingHistoryRawResponse {
  time: number;
  hash: string;
  delta: {
    type: string;
    coin: string;
    usdc: string;
    szi: string;
    fundingRate: string;
    nSamples?: number | null;
  };
}

// Flattened format for easier consumption
interface FundingHistoryResponse {
  time: number;
  coin: string;
  usdc: string;
  szi: string;
  fundingRate: string;
}

interface OrderStatusResponse {
  order: {
    coin: string;
    side: 'A' | 'B';
    limitPx: string;
    sz: string;
    oid: number;
    timestamp: number;
    origSz: string;
  };
  status: string;
  statusTimestamp: number;
}

interface UserStateResponse {
  assetPositions: Array<{
    position: {
      coin: string;
      entryPx: string;
      leverage: {
        type: string;
        value: number;
      };
      liquidationPx: string | null;
      marginUsed: string;
      positionValue: string;
      returnOnEquity: string;
      szi: string;
      unrealizedPnl: string;
      cumFunding?: {
        allTime: string;
        sinceChange: string;
        sinceOpen: string;
      };
    };
    type: string;
  }>;
  crossMarginSummary: {
    accountValue: string;
    totalMarginUsed: string;
    totalNtlPos: string;
    totalRawUsd: string;
  };
  marginSummary: {
    accountValue: string;
    totalMarginUsed: string;
    totalNtlPos: string;
    totalRawUsd: string;
  };
  withdrawable: string;
}

export class HyperliquidClient {
  private apiUrl: string;
  private marketsCache: Hip3Market[] = [];
  private cacheTimestamp: number = 0;
  private cacheDuration: number = 60000; // 1 minute

  constructor(apiUrl = HYPERLIQUID_API) {
    this.apiUrl = apiUrl;
  }

  private async post<T>(data: object): Promise<T> {
    const response = await fetch(this.apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Hyperliquid API error: ${response.statusText}`);
    }

    return response.json();
  }

  // Discover all available markets (native + HIP-3)
  async discoverMarkets(): Promise<Hip3Market[]> {
    // Return cached markets if still valid
    if (this.marketsCache.length > 0 && Date.now() - this.cacheTimestamp < this.cacheDuration) {
      return this.marketsCache;
    }

    try {
      // Step 1: Get all perpetual DEXs
      console.log('Fetching all perpetual DEXs...');
      const dexsResponse = await this.post<Array<null | {
        name: string;
        fullName: string;
        deployer: string;
        oracleUpdater: string | null;
        feeRecipient: string | null;
        assetToStreamingOiCap?: Array<[string, string]>;
        assetToFundingMultiplier?: Array<[string, string]>;
      }>>({
        type: 'perpDexs',
      });

      console.log('Found DEXs:', dexsResponse.length, 'total (including native)');
      console.log('HIP-3 DEXs:', dexsResponse.slice(1).map(d => d?.name || 'unknown'));

      const allMarkets: Hip3Market[] = [];

      // Step 2: Fetch markets for each DEX (including native)
      for (let i = 0; i < dexsResponse.length; i++) {
        const dex = dexsResponse[i];
        const isNative = i === 0; // First element (null) is native Hyperliquid
        const dexName = isNative ? 'hyperliquid' : (dex?.name || `dex-${i}`);

        console.log(`Fetching markets for ${isNative ? 'native Hyperliquid' : `HIP-3 DEX: ${dexName}`}...`);

        try {
          // Build request - native uses empty string, HIP-3 uses dex name
          const request: any = { type: 'metaAndAssetCtxs' };
          if (!isNative && dex?.name) {
            request.dex = dex.name; // Use DEX name string, not index
          }

          const response = await this.post<[{ universe: any[] }, any[]]>(request);
          const [meta] = response;

          console.log(`  Found ${meta.universe.length} assets in ${dexName}`);

          for (const asset of meta.universe) {
            if (asset.isDelisted) continue;

            // According to Hyperliquid API docs:
            // - Native: asset.name = "BTC", "ETH" (simple ticker)
            // - HIP-3: asset.name = "xyz:XYZ100" (already prefixed)

            // Log first asset to verify API behavior
            if (meta.universe.indexOf(asset) === 0) {
              console.log(`[Discovery] First asset in ${dexName}:`, asset.name);
            }

            // Extract base asset (remove dex prefix if present)
            let baseAsset: string;
            let symbol: string;

            if (asset.name.includes(':')) {
              // HIP-3 format: "xyz:XYZ100"
              const parts = asset.name.split(':');
              baseAsset = parts[1];  // "XYZ100"
              symbol = asset.name;    // "xyz:XYZ100" (use as-is from API)
            } else {
              // Native format: "BTC"
              baseAsset = asset.name;
              symbol = asset.name;
            }

            allMarkets.push({
              id: symbol,
              symbol: symbol,
              provider: dexName,
              name: `${baseAsset} Perpetual`,
              baseAsset,
              quoteAsset: 'USDC',
              maxLeverage: asset.maxLeverage,
              szDecimals: asset.szDecimals,
              onlyIsolated: asset.onlyIsolated,
              isNative,
              dexIndex: isNative ? undefined : dex?.name,
            });
          }
        } catch (error) {
          console.error(`Error fetching markets for DEX ${dexName}:`, error);
        }
      }

      this.marketsCache = allMarkets;
      this.cacheTimestamp = Date.now();

      const hip3Markets = allMarkets.filter(m => !m.isNative);
      console.log('=== Market Discovery Complete ===');
      console.log('Total markets:', allMarkets.length);
      console.log('Native Hyperliquid markets:', allMarkets.filter(m => m.isNative).length);
      console.log('HIP-3 markets:', hip3Markets.length);
      console.log('HIP-3 examples:', hip3Markets.slice(0, 10).map(m => ({
        symbol: m.symbol,
        provider: m.provider,
        baseAsset: m.baseAsset
      })));

      return allMarkets;
    } catch (error) {
      console.error('Error discovering markets:', error);
      return [];
    }
  }

  // Get all markets for a specific base asset
  async getMarketsForAsset(baseAsset: string): Promise<Hip3Market[]> {
    const allMarkets = await this.discoverMarkets();
    return allMarkets.filter((m) => m.baseAsset.toUpperCase() === baseAsset.toUpperCase());
  }

  // Get meta and asset contexts for a specific DEX
  async getMetaAndAssetCtxs(dex?: string): Promise<[{ universe: any[] }, any[]]> {
    return this.post<[{ universe: any[] }, any[]]>({
      type: 'metaAndAssetCtxs',
      ...(dex && { dex }),
    });
  }

  async getAllMids(dex?: string): Promise<AllMidsResponse> {
    return this.post<AllMidsResponse>({
      type: 'allMids',
      ...(dex && { dex }),
    });
  }

  async getL2Book(coin: string, nSigFigs = 5, dex?: string): Promise<L2BookResponse> {
    return this.post<L2BookResponse>({
      type: 'l2Book',
      coin,
      nSigFigs,
      ...(dex && { dex }),
    });
  }

  async getOpenOrders(user: string, dex?: string): Promise<OpenOrderResponse[]> {
    return this.post<OpenOrderResponse[]>({
      type: 'openOrders',
      user,
      ...(dex && { dex }),
    });
  }

  async getUserFills(user: string): Promise<UserFillResponse[]> {
    return this.post<UserFillResponse[]>({
      type: 'userFills',
      user,
    });
  }

  async getUserFunding(user: string, startTime?: number, endTime?: number): Promise<FundingHistoryResponse[]> {
    const rawResponse = await this.post<FundingHistoryRawResponse[]>({
      type: 'userFunding',
      user,
      startTime: startTime || Date.now() - 30 * 24 * 60 * 60 * 1000, // Last 30 days
      endTime: endTime || Date.now(),
    });

    // Flatten the nested delta structure for easier consumption
    return rawResponse.map(entry => ({
      time: entry.time,
      coin: entry.delta.coin,
      usdc: entry.delta.usdc,
      szi: entry.delta.szi,
      fundingRate: entry.delta.fundingRate,
    }));
  }

  async getHistoricalOrders(user: string): Promise<OrderStatusResponse[]> {
    return this.post<OrderStatusResponse[]>({
      type: 'historicalOrders',
      user,
    });
  }

  async getUserState(user: string): Promise<UserStateResponse> {
    return this.post<UserStateResponse>({
      type: 'clearinghouseState',
      user,
    });
  }

  // Helper method to get quote from L2 book
  async getQuote(market: Hip3Market): Promise<Quote | null> {
    try {
      const l2Book = await this.getL2Book(market.symbol, 5, market.dexIndex);
      const [bids, asks] = l2Book.levels;

      if (bids.length === 0 || asks.length === 0) {
        return null;
      }

      const bestBid = bids[0];
      const bestAsk = asks[0];

      const bidPrice = parseFloat(bestBid.px);
      const askPrice = parseFloat(bestAsk.px);

      return {
        marketId: market.id,
        provider: market.provider,
        bidPrice,
        askPrice,
        bidSize: parseFloat(bestBid.sz),
        askSize: parseFloat(bestAsk.sz),
        timestamp: l2Book.time,
        spread: askPrice - bidPrice,
      };
    } catch (error) {
      console.error(`Error fetching quote for ${market.symbol} (dex: ${market.dexIndex}):`, error);
      return null;
    }
  }

  // Get quotes for multiple markets
  async getQuotes(markets: Hip3Market[]): Promise<Quote[]> {
    const quotePromises = markets.map((market) => this.getQuote(market));
    const quotes = await Promise.all(quotePromises);
    return quotes.filter((q): q is Quote => q !== null);
  }

  // Convert user fills to positions (simplified)
  async getUserPositions(user: string): Promise<Position[]> {
    try {
      const fills = await this.getUserFills(user);
      // In a real implementation, we'd aggregate fills into positions
      // This is a simplified placeholder
      const positionMap = new Map<string, Position>();

      fills.forEach((fill) => {
        const key = fill.coin;
        if (!positionMap.has(key)) {
          positionMap.set(key, {
            id: `${user}-${fill.coin}`,
            marketId: fill.coin,
            provider: 'hyperliquid', // Would extract from HIP-3 prefix
            symbol: fill.coin,
            side: parseFloat(fill.startPosition) > 0 ? 'long' : 'short',
            size: Math.abs(parseFloat(fill.sz)),
            entryPrice: parseFloat(fill.px),
            currentPrice: parseFloat(fill.px),
            leverage: 1, // Would need to fetch from user state
            pnl: parseFloat(fill.closedPnl),
            pnlPercent: 0,
            timestamp: fill.time,
          });
        }
      });

      return Array.from(positionMap.values());
    } catch (error) {
      console.error('Error fetching user positions:', error);
      return [];
    }
  }
}

export const hyperliquidClient = new HyperliquidClient();
